import "./ImportExportSettingsPane.css";
import "./settings-ui.css";
import { useEffect, useState } from "preact/hooks";
import { DashboardPage, createDashboardLayout } from "../../components/DashboardPage/DashboardPage.jsx";
import { FileDropZone } from "../../components/FileDropZone/FileDropZone.jsx";
import { Panel } from "../../components/Panel/Panel.jsx";
import { SegmentedControl } from "../../components/SegmentedControl/SegmentedControl.jsx";
import { StatusMessage } from "../../components/StatusMessage/StatusMessage.jsx";
import { localizeErrorMessage } from "../../lib/localizeErrorMessage.js";
import { describeTimeAgo } from "../../lib/timeAgo.js";
import {
  buildProfileBackupFileName,
  clearActiveProfileBackupTarget,
  chooseActiveProfileBackupTarget,
  getActiveProfileBackupInterval,
  getActiveProfileBackupTarget,
  reauthorizeActiveProfileBackupTarget,
  setActiveProfileBackupInterval,
  writeActiveProfileBackupToStoredTarget
} from "../../lib/storage/profileBackupFile.js";
import { downloadTextFile } from "./tabularExport.js";
import {
  exportActiveProfileBackup,
  restoreProfileBackup
} from "../../lib/storage/walletWatchDb.js";

const automaticBackupIntervalOptions = [
  { value: "15m", label: "15m" },
  { value: "30m", label: "30m" },
  { value: "1h", label: "1h" },
  { value: "4h", label: "4h" },
  { value: "1d", label: "1d" }
];

const backupPaneLayout = createDashboardLayout({
  columns: [
    { size: "fill" },
    { size: "fill" }
  ],
  rows: [
    { size: "fill" }
  ],
  areas: [
    ["automatic", "secondary"]
  ]
});

const backupSecondaryLayout = createDashboardLayout({
  columns: [
    { size: "fill" }
  ],
  rows: [
    { size: "fixed", value: "auto" },
    { size: "fill" }
  ],
  areas: [
    ["manual"],
    ["restore"]
  ]
});

function footerToneClass(type) {
  if (type === "success") {
    return "tone-success";
  }

  if (type === "warning") {
    return "tone-warning";
  }

  if (type === "error") {
    return "tone-error";
  }

  return "";
}

export function BackupSettingsPane({ activeScope, onScopeChanged, t = (key) => key }) {
  const [automaticBackupStatus, setAutomaticBackupStatus] = useState(null);
  const [backupTargetName, setBackupTargetName] = useState("");
  const [backupInterval, setBackupInterval] = useState("1h");
  const [lastAutomaticBackupAt, setLastAutomaticBackupAt] = useState(null);
  const [backupNeedsAttention, setBackupNeedsAttention] = useState(false);
  const [manualBackupStatus, setManualBackupStatus] = useState(null);
  const [restoreStatus, setRestoreStatus] = useState(null);
  const lastBackupDescription = lastAutomaticBackupAt
    ? describeTimeAgo(lastAutomaticBackupAt)
    : null;

  useEffect(() => {
    let isMounted = true;

    async function loadBackupTarget() {
      const [target, interval] = await Promise.all([
        getActiveProfileBackupTarget(),
        getActiveProfileBackupInterval()
      ]);

      if (!isMounted) {
        return;
      }

      setBackupTargetName(target.handle?.name ?? "");
      setBackupInterval(interval);
      setLastAutomaticBackupAt(target.lastSavedAt ?? null);
      setBackupNeedsAttention(target.needsAttention || target.permission !== "granted");
    }

    loadBackupTarget().catch((error) => {
      console.error("Failed to load stored profile backup folder.", error);
    });

    return () => {
      isMounted = false;
    };
  }, [activeScope?.profileId]);

  async function handleChooseAutomaticBackupTarget() {
    try {
      setAutomaticBackupStatus({
        type: "pending",
        message: t("messages.backupSettings.openingFolderPicker")
      });

      const target = await chooseActiveProfileBackupTarget();
      setBackupTargetName(target.fileName);
      setBackupNeedsAttention(false);

      setAutomaticBackupStatus({
        type: "pending",
        message: `${t("messages.backupSettings.writingFirstBackupInto")} ${target.fileName}...`
      });

      const result = await writeActiveProfileBackupToStoredTarget("setup-automatic-backup");

      if (result.status === "denied") {
        setBackupNeedsAttention(true);
        setAutomaticBackupStatus({
          type: "error",
          message: t("messages.backupSettings.writePermissionNotGranted")
        });
        return;
      }

      setLastAutomaticBackupAt(result.savedAt ?? null);
      setBackupNeedsAttention(false);
      setAutomaticBackupStatus({
        type: "success",
        message: `${t("messages.backupSettings.automaticBackupSetUpIn")} ${result.targetName || target.fileName}.`
      });
    } catch (error) {
      if (error?.name === "AbortError") {
        setAutomaticBackupStatus({
          type: "error",
          message: t("messages.backupSettings.backupFolderSelectionCancelled")
        });
        return;
      }

      setAutomaticBackupStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.backupSettings.failedToChooseBackupFolder")
      });
    }
  }

  async function handleAutomaticBackupNow() {
    try {
      if (!backupTargetName) {
        await handleChooseAutomaticBackupTarget();
        return;
      }

      setAutomaticBackupStatus({
        type: "pending",
        message: `${t("messages.backupSettings.writingBackupInto")} ${backupTargetName}...`
      });

      const result = await writeActiveProfileBackupToStoredTarget("manual-automatic-backup");

      if (result.status === "denied") {
        setBackupNeedsAttention(true);
        setAutomaticBackupStatus({
          type: "error",
          message: t("messages.backupSettings.writePermissionNotGranted")
        });
        return;
      }

      if (result.status === "missing") {
        setAutomaticBackupStatus({
          type: "error",
          message: t("messages.backupSettings.noBackupFolderConfiguredYet")
        });
        return;
      }

      setLastAutomaticBackupAt(result.savedAt ?? null);
      setBackupNeedsAttention(false);
      setAutomaticBackupStatus({
        type: "success",
        message: `${t("messages.backupSettings.backedUpActiveProfileTo")} ${result.fileName} ${t("messages.backupSettings.in")} ${result.targetName || backupTargetName}.`
      });
    } catch (error) {
      if (error?.name === "AbortError") {
        setAutomaticBackupStatus({
          type: "error",
          message: t("messages.backupSettings.backupWasCancelled")
        });
        return;
      }

      setAutomaticBackupStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.backupSettings.failedToWriteBackup")
      });
    }
  }

  async function handleManualBackup() {
    try {
      setManualBackupStatus({
        type: "pending",
        message: t("messages.backupSettings.preparingBackup")
      });

      const backup = await exportActiveProfileBackup();
      const filename = buildProfileBackupFileName(
        backup.profile?.name,
        backup.activeProfileId,
        backup.activeYear
      );

      downloadTextFile(filename, `${JSON.stringify(backup, null, 2)}\n`, "application/json;charset=utf-8");
      setManualBackupStatus({
        type: "success",
        message: `${t("messages.backupSettings.savedManualBackupTo")} ${filename}.`
      });
    } catch (error) {
      setManualBackupStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.backupSettings.failedToCreateManualBackup")
      });
    }
  }

  async function handleClearAutomaticBackupTarget() {
    try {
      await clearActiveProfileBackupTarget();
      setBackupTargetName("");
      setLastAutomaticBackupAt(null);
      setBackupNeedsAttention(false);
      setAutomaticBackupStatus({
        type: "success",
        message: t("messages.backupSettings.disabledAutomaticBackup")
      });
    } catch (error) {
      setAutomaticBackupStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.backupSettings.failedToDisableAutomaticBackup")
      });
    }
  }

  async function handleReconnectAutomaticBackupTarget() {
    try {
      setAutomaticBackupStatus({
        type: "pending",
        message: t("messages.backupSettings.requestingBackupFolderPermission")
      });

      const result = await reauthorizeActiveProfileBackupTarget();

      if (result.status === "missing") {
        setAutomaticBackupStatus({
          type: "error",
          message: t("messages.backupSettings.noBackupFolderConfiguredYet")
        });
        return;
      }

      if (result.status === "denied") {
        setBackupNeedsAttention(true);
        setAutomaticBackupStatus({
          type: "error",
          message: t("messages.backupSettings.writePermissionNotGranted")
        });
        return;
      }

      setBackupNeedsAttention(false);
      setAutomaticBackupStatus({
        type: "success",
        message: `${t("messages.backupSettings.backupFolderAccessRestored")} ${result.targetName}.`
      });
    } catch (error) {
      if (error?.name === "AbortError") {
        setBackupNeedsAttention(true);
        setAutomaticBackupStatus({
          type: "error",
          message: t("messages.backupSettings.backupWasCancelled")
        });
        return;
      }

      setAutomaticBackupStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.backupSettings.failedToRestoreBackupFolderAccess")
      });
    }
  }

  async function handleBackupIntervalChange(nextInterval) {
    const previousInterval = backupInterval;
    setBackupInterval(nextInterval);

    try {
      const savedInterval = await setActiveProfileBackupInterval(nextInterval);
      setBackupInterval(savedInterval);
      setAutomaticBackupStatus({
        type: "success",
        message: `${t("messages.backupSettings.automaticBackupIntervalSetTo")} ${savedInterval}.`
      });
    } catch (error) {
      setBackupInterval(previousInterval);
      setAutomaticBackupStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.backupSettings.failedToSaveAutomaticBackupInterval")
      });
    }
  }

  async function handleRestoreFileSelection(file) {
    if (!file) {
      return;
    }

    try {
      setRestoreStatus({
        type: "pending",
        message: `${t("messages.backupSettings.reading")} ${file.name}...`
      });

      const text = await file.text();
      let payload;

      try {
        payload = JSON.parse(text);
      } catch (error) {
        throw new Error(t("messages.backupSettings.backupFileNotValidJson"), { cause: error });
      }

      setRestoreStatus({
        type: "pending",
        message: t("messages.backupSettings.restoringBackupIntoNewProfile")
      });

      const restored = await restoreProfileBackup(payload);
      await onScopeChanged?.(restored.scope);

      setRestoreStatus({
        type: "success",
        message: `${t("messages.backupSettings.restored")} ${restored.profile.name} ${t("messages.backupSettings.andSwitchedIntoIt")}`
      });
    } catch (error) {
      setRestoreStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.backupSettings.failedToRestoreBackup")
      });
    }
  }

  return (
    <DashboardPage
      layout={backupPaneLayout}
      slots={[
        {
          key: "automatic",
          area: "automatic",
          content: (
            <Panel
              padded
              className="settings-pane--compact"
              header={t("setUpAutomaticBackup")}
              footerClassName={footerToneClass(automaticBackupStatus?.type ?? (backupNeedsAttention ? "error" : ""))}
              footer={automaticBackupStatus ? (
                <StatusMessage type={automaticBackupStatus.type} message={automaticBackupStatus.message} />
              ) : backupNeedsAttention ? (
                <StatusMessage type="error" message={t("messages.backupSettings.automaticBackupNeedsPermission")} />
              ) : (
                lastAutomaticBackupAt
                  ? `${t("messages.backupSettings.lastBackup")}: ${t(lastBackupDescription.key, lastBackupDescription.params)}`
                  : t("messages.backupSettings.noAutomaticBackupWrittenYet")
              )}
            >
              <div class="settings-backup-pane">
                {backupTargetName ? (
                  <div class="settings-backup-target">
                    <div class="settings-field">
                      <span class="settings-field-label">{t("backupFolder")}</span>
                    </div>
                    <div class="settings-record">
                      <div class="settings-record-body">
                        <span class="settings-item-label">{backupTargetName}</span>
                      </div>
                      <div class="settings-record-actions">
                        {backupNeedsAttention ? (
                          <button
                            class="settings-action-button primary"
                            type="button"
                            onClick={handleReconnectAutomaticBackupTarget}
                          >
                            {t("reconnect")}
                          </button>
                        ) : null}
                        <button
                          class="settings-action-button danger"
                          type="button"
                          onClick={handleClearAutomaticBackupTarget}
                        >
                          {t("disable")}
                        </button>
                      </div>
                    </div>
                    <div class="settings-actions">
                      <button
                        class="settings-action-button"
                        type="button"
                        onClick={handleAutomaticBackupNow}
                      >
                        {t("backupNow")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div class="settings-backup-target">
                    <div class="settings-actions">
                      <button
                        class="settings-action-button primary"
                        type="button"
                        onClick={handleChooseAutomaticBackupTarget}
                      >
                        {t("selectBackupFolder")}
                      </button>
                    </div>
                  </div>
                )}

                {backupTargetName ? (
                  <label class="settings-field">
                    <span class="settings-field-label">{t("interval")}</span>
                    <SegmentedControl
                      options={automaticBackupIntervalOptions}
                      value={backupInterval}
                      onChange={handleBackupIntervalChange}
                    />
                  </label>
                ) : null}
              </div>
            </Panel>
          )
        },
        {
          key: "secondary",
          area: "secondary",
          content: (
            <DashboardPage
              layout={backupSecondaryLayout}
              slots={[
                {
                  key: "manual",
                  area: "manual",
                  fill: false,
                  content: (
                    <Panel
                      padded
                      className="settings-pane--compact"
                      header={t("manualBackup")}
                      footerClassName={footerToneClass(manualBackupStatus?.type)}
                      footer={manualBackupStatus ? (
                        <StatusMessage type={manualBackupStatus.type} message={manualBackupStatus.message} />
                      ) : (
                        t("messages.backupSettings.downloadFullJsonBackupOfActiveProfile")
                      )}
                    >
                      <div class="settings-actions">
                        <button
                          class="settings-action-button primary"
                          type="button"
                          onClick={handleManualBackup}
                        >
                          {t("saveBackupNow")}
                        </button>
                      </div>
                    </Panel>
                  )
                },
                {
                  key: "restore",
                  area: "restore",
                  content: (
                    <Panel
                      padded
                      className="settings-pane--compact"
                      header={t("restore")}
                      footerClassName={footerToneClass(restoreStatus?.type)}
                      footer={restoreStatus ? (
                        <StatusMessage type={restoreStatus.type} message={restoreStatus.message} />
                      ) : (
                        t("messages.backupSettings.createsNewProfileExistingDataStaysIntact")
                      )}
                    >
                      <div class="settings-backup-pane">
                        <FileDropZone
                          accept=".json,application/json"
                          label={t("messages.backupSettings.dragBackupJsonHere")}
                          note={t("messages.backupSettings.walletWatchBackupFileOnly")}
                          onFileSelected={handleRestoreFileSelection}
                        />
                      </div>
                    </Panel>
                  )
                }
              ]}
            />
          )
        }
      ]}
    />
  );
}
